import _ from "lodash";
import React, { useState, useEffect, useRef } from "react";
import classes from "./Table.scss";
import TableBody from "./TableBody";
import { useI18n } from "Hooks";
import Loading from "UIComponents/Loading";
import { scrollIntoView } from "Utils";
import {
  onAddFirstRow,
  onAddLastRow,
  columnIds,
  isAnyFilterSelected,
} from "GenericTableEditor/GenericTableEditorUtils";
import { SINGLE_LEVEL, MULTI_LEVEL } from "Fields/FieldUtils";
import { Checkbox, Tooltip, Button } from "@toddle-edu/ds-web";
import { AddCircleOutlined } from "@toddle-edu/ds-icons";
import PropTypes from "prop-types";
import classNames from "classnames";

const TableHeader = props => {
  const { t } = useI18n([
    "scopeAndSequence",
    "common",
    "academicSetup",
    "a11y",
  ]);
  const {
    mode,
    nodes,
    setNodes,
    rootNodeId,
    currentEditorConfig,
    isAddActionDisabled,
  } = props;

  const [isChecked, setIsChecked] = useState(false);
  const rootNodeChildren = _.get(nodes[rootNodeId], "children", []);
  const allowedColumns = currentEditorConfig.columns;
  const areAllChildrenChecked = _.reduce(
    _.filter(nodes, node => node.depth !== 0),
    (result, value) => {
      return result && _.get(value, "isChecked", false);
    },
    true
  );

  useEffect(() => {
    setIsChecked(areAllChildrenChecked);
  }, [areAllChildrenChecked]);

  if (_.isEmpty(rootNodeChildren) && isChecked) {
    setIsChecked(false);
  }
  const checkAllNodes = () => {
    if (!_.isEmpty(rootNodeChildren)) {
      if (!isChecked) {
        const newNodes = { ...nodes };
        _.forOwn(newNodes, (value, key) => {
          if (value.depth != 0) {
            newNodes[key] = { ...value, isChecked: true };
          }
        });
        setNodes(newNodes);
        setIsChecked(true);
      } else {
        const newNodes = { ...nodes };
        _.forOwn(newNodes, (value, key) => {
          if (value.depth != 0) {
            newNodes[key] = { ...value, isChecked: false };
          }
        });
        setNodes(newNodes);
        setIsChecked(false);
      }
    }
  };

  const handleAddFirstRow = () => {
    onAddFirstRow({ rootNodeId, nodes, setNodes });
  };

  return (
    <thead className={classes.tableHeader}>
      <tr className={classes.tableRow}>
        {_.map(allowedColumns, column => {
          const columnLabel = _.get(column, "label", "");
          const columnStyle = _.get(column, "style", {});
          const isRequired = _.get(column, "isRequired", false);
          const columHeader =
            t(columnLabel, { defaultValue: columnLabel }) +
            (isRequired ? "*" : "");

          switch (column.id) {
            case columnIds.selectableLabel:
            case columnIds.previewSelectableLabel:
            case columnIds.label: {
              const columnVariant = _.get(column, "variant", SINGLE_LEVEL);

              const addIconTooltipText = t("common:add");

              const columnHeaderContainerClasses = classNames({
                [classes.tableColumnForMultiLevelHeaderView]:
                  columnVariant === MULTI_LEVEL && mode === "view",
                [classes.tableColumnForMultiLevelHeaderEdit]:
                  columnVariant === MULTI_LEVEL && mode === "edit",
              });

              return (
                <th
                  className={classes.tableCell}
                  key={column.id}
                  style={columnStyle}
                >
                  <div
                    className={classes.learningStandardsHeader}
                    tabIndex={mode === "edit" && !isAddActionDisabled ? 0 : -1}
                  >
                    {mode === "edit" && !isAddActionDisabled && (
                      <div className={classes.actionButtonContainer}>
                        <div className={classes.addIconContainer}>
                          <button
                            className={classes.addIcon}
                            onClick={handleAddFirstRow}
                            aria-label={t("a11y:add_row")}
                          >
                            <Tooltip
                              tooltip={addIconTooltipText}
                              placement={"top"}
                            >
                              <AddCircleOutlined size={"xx-small"} />
                            </Tooltip>
                          </button>
                        </div>
                        <div className={classes.checkBoxContainer}>
                          <Checkbox
                            isChecked={isChecked}
                            onChange={checkAllNodes}
                            size="xx-small"
                          />
                        </div>
                      </div>
                    )}
                    <div className={columnHeaderContainerClasses}>
                      {columHeader}
                    </div>
                  </div>
                </th>
              );
            }
            case columnIds.code:
            case columnIds.grades:
            case columnIds.icon:
            case columnIds.tags:
            case columnIds.descriptor: {
              return (
                <th
                  className={classes.tableCell}
                  key={column.id}
                  style={columnStyle}
                >
                  {columHeader}
                </th>
              );
            }
          }
        })}
      </tr>
    </thead>
  );
};

const Table = props => {
  const {
    mode,
    nodes,
    setNodes,
    rootNodeId,
    currentEditorConfig,
    filters,
    tableContainerStyle = {},
  } = props;

  const { t } = useI18n(["common"]);

  const tableStyle = currentEditorConfig.tableStyle || {};

  const isAddActionDisabled = isAnyFilterSelected({ filters });

  const handleAddLastRow = () => {
    const newNode = onAddLastRow({ rootNodeId, nodes, setNodes });

    setTimeout(() => {
      scrollIntoView({
        element: document.getElementById(newNode.id),
        checkForElementInViewPort: true,
        block: "center",
      });
    }, 300);
  };

  const tableContainerRef = useRef(null);

  return (
    <div className={classes.tableContainer} style={tableContainerStyle}>
      {_.size(nodes) > 0 ? (
        <div className={classes.tableInnerContainer} ref={tableContainerRef}>
          <table className={classes.table} style={tableStyle} id={"TABLE"}>
            <TableHeader
              mode={mode}
              nodes={nodes}
              setNodes={setNodes}
              rootNodeId={rootNodeId}
              currentEditorConfig={currentEditorConfig}
              isAddActionDisabled={isAddActionDisabled}
            />
            <TableBody tableContainerRef={tableContainerRef} {...props} />
          </table>

          {mode === "edit" ? (
            <div className={classes.addLastRowButtonContainer}>
              <Button
                variant="inline-progressive"
                icon={<AddCircleOutlined />}
                onClick={handleAddLastRow}
                disabled={isAddActionDisabled}
              >
                {t("common:new_with_label", {
                  label: t("common:row"),
                })}
              </Button>
            </div>
          ) : null}
        </div>
      ) : (
        <div className={classes.loadingContainer}>
          <Loading />
        </div>
      )}
    </div>
  );
};

export default Table;

Table.propTypes = {
  mode: PropTypes.oneOf(["edit", "view"]).isRequired,
  nodes: PropTypes.object.isRequired,
  setNodes: PropTypes.func.isRequired,
  rootNodeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  maxDepth: PropTypes.number.isRequired,

  //THis object is used to control columns of the table and to define error behaviour of columns
  currentEditorConfig: PropTypes.object.isRequired,

  //Map of matching nodes(according to filters), where node ids are mapped with themselves as keys
  filteredNodeIds: PropTypes.object.isRequired,
  sortedNodeIds: PropTypes.arrayOf(PropTypes.string),
};

Table.defaultProps = {
  sortedNodeIds: [],
};
